import type { ReactNode } from 'react';

interface ActionItem {
  id: string;
  icon: string;
  label: string;
  bgColor?: string;
  iconColor?: string;
  badgeCount?: number;
  disabled?: boolean;
  onClick?: () => void;
  /** Optional custom rendered element to replace default icon+label */
  render?: () => ReactNode;
}

interface ActionIconGridProps {
  actions: ActionItem[];
  columns?: 3 | 4 | 6;
  className?: string;
  'data-product-shop'?: string;
}

const COL_CLASSES: Record<number, string> = {
  3: 'grid-cols-3 sm:grid-cols-3',
  4: 'grid-cols-4 sm:grid-cols-4',
  6: 'grid-cols-3 sm:grid-cols-6',
};

export function ActionIconGrid({
  actions,
  columns = 3,
  className = '',
  'data-product-shop': dataProductShop,
}: ActionIconGridProps) {
  const gridClass = COL_CLASSES[columns];

  return (
    <div className={`grid ${gridClass} gap-x-2 gap-y-3 ${className}`} data-product-shop={dataProductShop}>
      {actions.map((action) => (
        <button
          key={action.id}
          type="button"
          disabled={action.disabled}
          onClick={action.onClick}
          className={`group flex flex-col items-center gap-2 rounded-2xl active:scale-[0.96] transition-all duration-300 min-h-[96px] px-1.5 py-2 justify-center hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_12px_28px_rgba(15,23,42,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-agrida-500/40 ${
            action.disabled ? 'opacity-40 pointer-events-none' : ''
          }`}
        >
          <span
            className={`w-12 h-12 rounded-2xl flex items-center justify-center relative shrink-0 transition-all duration-300 group-hover:scale-105 group-hover:shadow-[0_8px_20px_rgba(22,148,94,0.12)] ${
              action.bgColor || 'bg-slate-100'
            }`}
          >
            {action.render ? (
              action.render()
            ) : (
              <i className={`${action.icon} ${action.iconColor || 'text-slate-500'} text-xl`} />
            )}
            {action.disabled && (
              <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-slate-400 text-white rounded-full flex items-center justify-center">
                <i className="ri-lock-line text-[8px]" />
              </span>
            )}
            {!action.disabled && action.badgeCount !== undefined && action.badgeCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 shadow-sm animate-pulse-dot">
                {action.badgeCount > 99 ? '99+' : action.badgeCount > 9 ? '9+' : action.badgeCount}
              </span>
            )}
          </span>
          <span className={`text-[12px] font-semibold text-center leading-[1.3] w-full max-w-[96px] text-safe-normal line-clamp-2 ${
            action.disabled ? 'text-slate-300' : 'text-slate-600'
          }`}>
            {action.label}
          </span>
        </button>
      ))}
    </div>
  );
}
